<script setup lang="ts">
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'

import AppChips from '@/components/app/AppChips.vue'
import AppChipsItem from '@/components/app/AppChipsItem.vue'
import AppLoadingWrapper from '@/components/app/AppLoadingWrapper.vue'
import AppHeading from '@/components/app/AppHeading.vue'
import AppSubItem from '@/components/app/AppSubItem.vue'
import { useGeoDataStore } from '~/stores/geoData'
import { ElectricityGridOwner } from '~/types/statistics'
import { Municipality } from '~/types/types'

const props = defineProps<{ data: ElectricityGridOwner; showMunicipalities?: boolean }>()
const geoDataStore = useGeoDataStore()
const municipalities = computed(() => {
  if (geoDataStore.municipalities.length === 0) {
    return null
  }
  return props.data.municipalities.reduce((list, municipalityCode) => {
    if (!list.some((municipality) => municipality.municipality_code === municipalityCode)) {
      const municipality = geoDataStore.getMuniciplityByMunicipalityId(municipalityCode)
      municipality && list.push(municipality)
    }
    return list.sort((a, b) => a.name.localeCompare(b.name))
  }, [] as Municipality[])
})
</script>
<template>
  <Accordion :active-index="null">
    <AccordionTab
      :pt="{
        root: {
          class: { 'app-accordion': true },
        },
      }"
    >
      <template #header>
        <div class="header">
          <AppHeading :title="data.company" type="h3" size="large" />
          <AppChips>
            <AppChipsItem :item="`${data.areas.length} st elnät`" />
          </AppChips>
        </div>
      </template>
      <div class="electricity-grid-owner">
        <AppHeading :title="data.company" type="h3" size="large" />
        <div class="contact">
          <div v-if="data.website">
            <a :href="`https://${data.website}`" target="_blank">{{ data.website }}</a>
          </div>
          <div v-if="data.address">{{ data.address }}</div>
          <div v-if="data.phone">{{ data.phone }}</div>
          <div v-if="data.email">
            <a :href="`mailto:${data.email}`">{{ data.email }}</a>
          </div>
        </div>
        <div v-if="showMunicipalities">
          <AppSubItem :title="`Finns i ${municipalities?.length} kommuner`">
            <AppLoadingWrapper :is-loading="!municipalities" :height="20" position="left">
              <AppChips>
                <NuxtLink
                  v-for="municipality in municipalities"
                  :key="municipality.municipality_code"
                  :to="`/utforska-en-plats/kommun/${municipality.slug}`"
                >
                  <span>{{ municipality.name }}</span>
                </NuxtLink>
              </AppChips>
            </AppLoadingWrapper>
          </AppSubItem>
        </div>
      </div>
    </AccordionTab>
  </Accordion>
</template>
<style lang="scss">
.app-accordion {
  .p-accordion-content {
    border: none;
  }
  .p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link {
    background-color: transparent;
    border: none;
  }
  .p-accordion-header .p-accordion-header-link {
    background-color: transparent;
    border: none;
  }
  .p-accordion-header .p-accordion-header-link {
    padding: 0.5rem 0.5rem;
    border: none;
    border-radius: 0;
    margin-left: -0.5rem;
  }
  .p-accordion-tab:first-child .p-accordion-header .p-accordion-header-link {
    border-top-right-radius: 0px;
    border-top-left-radius: 0px;
  }
}
</style>
<style lang="scss" scoped>
@import 'App.scss';

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.contact {
  margin-top: -0.25rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}
.electricity-grid-owner {
  padding: 1rem 0;
}
.p-accordion .p-accordion-header .p-accordion-header-link {
  background-color: transparent;
  border: none;
}
</style>
```
